/**
 * DashboardUpdateModal
 *
 * Console-style modal for triggering and monitoring a dashboard update.
 * Handles the service restart by switching to recovery polling when
 * the SSE stream disconnects.
 *
 * Features "Idle UX" animation during service restart to give users
 * visual feedback that the system is working even when no new log
 * output is being received.
 */

import { useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { Button } from "../ui/Button";
import { Icon } from "../ui/Icon";
import { Spinner } from "../ui/Spinner";
import { cn } from "../../utils/cn";
import { useDashboardUpdate, type UpdatePhase } from "../../hooks/useDashboardUpdate";
import type { DashboardChannel } from "../../hooks/useSystemMeta";
import { useConsoleScroll } from "../../hooks/useConsoleScroll";
import { ConsolePanel } from "./ConsolePanel";

// ── Idle UX Messages ────────────────────────────────────────────────────────

const IDLE_MESSAGES = [
  { delay: 3000, text: "       Waiting for service to stop..." },
  { delay: 8000, text: "       Installing dependencies..." },
  { delay: 15000, text: "       Building application..." },
  { delay: 25000, text: "       Applying database migrations..." },
  { delay: 35000, text: "       Starting service..." },
  { delay: 50000, text: "       Running health checks..." },
  { delay: 70000, text: "       Still waiting... this may take a moment." },
  { delay: 90000, text: "       Update is taking longer than expected..." },
];

// ── Terminal Cursor Component ───────────────────────────────────────────────

const TerminalCursor = ({ active }: { active: boolean }) => {
  if (!active) return null;
  return (
    <span className="inline-block w-2 h-4 bg-emerald-400/80 animate-pulse ml-0.5 align-middle" />
  );
};

type DashboardUpdateModalProps = {
  open: boolean;
  onClose: () => void;
  currentCommitShort: string | null;
  latestCommitShort: string | null;
  currentVersion: string | null;
  latestVersion: string | null;
  channel?: DashboardChannel;
};

const PHASE_BADGES: Record<UpdatePhase, { label: string; className: string }> = {
  idle: { label: "Ready", className: "bg-muted text-muted-foreground" },
  starting: { label: "Starting", className: "bg-yellow-500/20 text-yellow-400" },
  streaming: { label: "Updating", className: "bg-blue-500/20 text-blue-400 animate-pulse" },
  reconnecting: { label: "Restarting", className: "bg-amber-500/20 text-amber-400 animate-pulse" },
  verifying: { label: "Verifying", className: "bg-blue-500/20 text-blue-400 animate-pulse" },
  done: { label: "Complete", className: "bg-emerald-500/20 text-emerald-400" },
  version_unchanged: { label: "Needs Attention", className: "bg-amber-500/20 text-amber-400" },
  failed: { label: "Failed", className: "bg-red-500/20 text-red-400" },
};

const isActivePhase = (phase: UpdatePhase): boolean =>
  phase === "starting" || phase === "streaming" || phase === "reconnecting" || phase === "verifying";

export const DashboardUpdateModal = ({
  open,
  onClose,
  currentCommitShort,
  latestCommitShort,
  currentVersion,
  latestVersion,
  channel,
}: DashboardUpdateModalProps) => {
  const { phase, lines, error, newCommit, newVersion, isReinstall, startUpdate, reset } = useDashboardUpdate({
    currentCommitShort,
    currentVersion: currentVersion ?? null,
    channel,
  });

  const displayCurrent = currentVersion && currentVersion !== "unknown" ? `v${currentVersion}` : currentCommitShort;
  const displayLatest = latestVersion ? `v${latestVersion}` : latestCommitShort;
  const displayNewVersion = newVersion && newVersion !== "unknown" ? `v${newVersion}` : newCommit;
  const titleId = useId();

  // Idle UX state
  const [idleLines, setIdleLines] = useState<string[]>([]);
  const idleTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  const scroll = useConsoleScroll(lines.length + idleLines.length);

  // Reset when modal opens
  useEffect(() => {
    if (open) {
      reset();
      setIdleLines([]);
    }
  }, [open, reset]);

  // Idle UX: Show placeholder messages during reconnecting phase
  useEffect(() => {
    idleTimersRef.current.forEach((t) => clearTimeout(t));
    idleTimersRef.current = [];

    if (phase === "reconnecting") {
      setIdleLines([]);
      IDLE_MESSAGES.forEach(({ delay, text }) => {
        const timer = setTimeout(() => {
          setIdleLines((prev) => [...prev, text]);
        }, delay);
        idleTimersRef.current.push(timer);
      });
    } else if (phase === "verifying" || phase === "done" || phase === "version_unchanged" || phase === "failed") {
      setIdleLines([]);
    }

    return () => {
      idleTimersRef.current.forEach((t) => clearTimeout(t));
      idleTimersRef.current = [];
    };
  }, [phase]);

  const handleCopyOutput = useCallback(() => {
    scroll.handleCopyOutput(lines);
  }, [lines, scroll]);

  const handleClose = useCallback(() => {
    if (isActivePhase(phase)) return;
    reset();
    onClose();
  }, [phase, reset, onClose]);

  const handleReload = useCallback(() => {
    window.location.reload();
  }, []);

  if (!open) return null;

  // Use a friendlier badge for pre-release reinstalls
  const badge = phase === "version_unchanged" && isReinstall
    ? { label: "Reinstalled", className: "bg-blue-500/20 text-blue-400" }
    : PHASE_BADGES[phase];
  const showConfirm = phase === "idle";
  const showConsole = phase !== "idle";
  const isTerminal = phase === "done" || phase === "version_unchanged" || phase === "failed";

  // Idle UX + terminal cursor rendered as afterLines slot
  const afterLinesContent = (
    <>
      {phase === "reconnecting" && idleLines.length > 0 && (
        <div className="mt-2 border-t border-white/[0.06] pt-2">
          {idleLines.map((line, i) => (
            <div
              key={`idle-${i}`}
              className="text-muted-foreground/40 animate-in fade-in-50 duration-500"
            >
              {line}
            </div>
          ))}
        </div>
      )}
      {isActivePhase(phase) && (
        <div className="inline-flex items-center mt-1">
          <span className="text-muted-foreground/30 select-none">{">"}</span>
          <TerminalCursor active />
        </div>
      )}
    </>
  );

  const modal = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-in fade-in-0 duration-200"
        onClick={isTerminal || showConfirm ? handleClose : undefined}
      />
      <div role="dialog" aria-modal="true" aria-labelledby={titleId} className="relative w-full max-w-3xl mx-4 animate-in fade-in-0 zoom-in-95 duration-200">
        <div className="rounded-xl border border-white/[0.08] bg-card shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-white/[0.06]">
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/[0.04]">
                <Icon icon="lucide:download" className="h-4 w-4 text-foreground" />
              </div>
              <div>
                <h2 id={titleId} className="text-sm font-semibold text-foreground">Dashboard Update</h2>
                <p className="text-[10px] text-muted-foreground/70">System Response</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className={cn("inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-medium", badge.className)}>
                {badge.label}
              </span>
              <button
                type="button"
                onClick={handleClose}
                disabled={isActivePhase(phase)}
                aria-label="Close dialog"
                className="flex h-7 w-7 items-center justify-center rounded-md hover:bg-white/[0.06] transition-colors disabled:opacity-40"
              >
                <Icon icon="lucide:x" className="h-4 w-4 text-muted-foreground" />
              </button>
            </div>
          </div>

          {/* Confirmation view */}
          {showConfirm && (
            <div className="px-5 py-6 space-y-4">
              <p className="text-sm text-foreground">
                Update the dashboard to the latest version{displayLatest ? ` (${displayLatest})` : ""}?
              </p>

              <div className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2">
                <Icon icon="lucide:alert-triangle" className="h-4 w-4 text-amber-400 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-amber-200/90 space-y-1">
                  <p>The dashboard service will restart during the update. You will temporarily lose connection.</p>
                  <p>The page will automatically reconnect when the update completes.</p>
                </div>
              </div>

              {displayCurrent && (
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <span>Current: <code className="rounded bg-muted/60 px-1.5 py-[2px] font-mono text-[10px]">{displayCurrent}</code></span>
                  {displayLatest && (
                    <>
                      <Icon icon="lucide:arrow-right" className="h-3 w-3" />
                      <span>Latest: <code className="rounded bg-muted/60 px-1.5 py-[2px] font-mono text-[10px]">{displayLatest}</code></span>
                    </>
                  )}
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="ghost" size="sm" onClick={handleClose}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => void startUpdate()}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                >
                  <Icon icon="lucide:download" className="h-3.5 w-3.5 mr-1.5" />
                  Update dashboard
                </Button>
              </div>
            </div>
          )}

          {/* Console output view */}
          {showConsole && (
            <>
              <ConsolePanel
                lines={lines}
                consoleRef={scroll.consoleRef}
                onScroll={scroll.handleScroll}
                wrapLines={scroll.wrapLines}
                onToggleWrap={() => scroll.setWrapLines((prev) => !prev)}
                autoScroll={scroll.autoScroll}
                onScrollToBottom={scroll.scrollToBottom}
                onCopy={handleCopyOutput}
                isLoading={isActivePhase(phase)}
                afterLines={afterLinesContent}
              />

              {/* Reconnecting overlay */}
              {(phase === "reconnecting" || phase === "verifying") && (
                <div className="flex items-center gap-3 px-5 py-3 border-t border-white/[0.06] bg-amber-500/5">
                  <Spinner size="4" className="text-amber-400" />
                  <span className="text-xs text-amber-400">
                    {phase === "reconnecting"
                      ? "Dashboard is restarting. Waiting for recovery..."
                      : "Verifying update..."}
                  </span>
                </div>
              )}

              {phase === "done" && (
                <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-white/[0.06]">
                  <div className="text-xs text-muted-foreground/70">
                    Update complete.{displayNewVersion ? ` Version: ${displayNewVersion}` : ""}
                  </div>
                  <Button variant="primary" size="sm" onClick={handleReload}>
                    Reload dashboard
                  </Button>
                </div>
              )}

              {phase === "version_unchanged" && isReinstall && (
                <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-white/[0.06]">
                  <div className="flex items-start gap-2 flex-1">
                    <Icon icon="lucide:info" className="h-4 w-4 text-blue-400 mt-0.5 flex-shrink-0" />
                    <div className="text-xs text-muted-foreground/90 space-y-1">
                      <p>Reinstall complete. Version unchanged ({displayNewVersion ?? "unknown"}).</p>
                      <p>This is normal when reinstalling the same pre-release version.</p>
                    </div>
                  </div>
                  <Button variant="primary" size="sm" onClick={handleReload}>
                    Reload dashboard
                  </Button>
                </div>
              )}

              {phase === "version_unchanged" && !isReinstall && (
                <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-white/[0.06] bg-amber-500/5">
                  <div className="flex items-start gap-2 flex-1">
                    <Icon icon="lucide:alert-triangle" className="h-4 w-4 text-amber-400 mt-0.5 flex-shrink-0" />
                    <div className="text-xs text-amber-200/90 space-y-1">
                      <p>Update completed but version unchanged ({displayNewVersion ?? "unknown"}).</p>
                      <p>The update may not have been applied. Check logs for errors.</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={handleCopyOutput}>
                      Copy logs
                    </Button>
                    <Button variant="secondary" size="sm" onClick={handleReload}>
                      Reload anyway
                    </Button>
                  </div>
                </div>
              )}

              {phase === "failed" && (
                <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-white/[0.06]">
                  <div className="text-xs text-red-400/80 flex-1 min-w-0 truncate">
                    {error ?? "Update failed."}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={handleCopyOutput}>
                      Copy logs
                    </Button>
                    <Button variant="secondary" size="sm" onClick={handleClose}>
                      Close
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};
